import { Typography, colors } from '@sweep-io/sweep-design';
import { Stack, Collapse } from '@mui/material';
import { useState, useEffect } from 'react';
import {
  DataTableBaseColumn,
  DataTableRow,
  DataTableVariant,
} from '../../../common/table/TableTypes';
import { DataTableId } from '../../../common/table/utils';
import { VirtualScrollDataTable } from '../../../common/table/VirtualScrollDataTable';
import { DataTable } from '../../../common/table/DataTable';
import { getNameWithPlural } from '../../tabs/getMetadataLabel';
import { ExpandButtonSimple } from '../../../common/ExpandButtonSimple';
import { useSearchContext } from '../../building-blocks/documentation-card/search/SearchContext';
import { SearchResult } from '../../building-blocks/documentation-card/search/SearchContext';

export interface TableWithHeaderProps {
  tableHeader: string;
  columns: DataTableBaseColumn[];
  rows: DataTableRow[];
  onRowClick?: (
    e: React.MouseEvent<HTMLTableCellElement, MouseEvent>,
    row: DataTableRow,
    columnData: DataTableBaseColumn,
  ) => void;
  dataTableType?: string;
  section?: SearchResult['section'];
  forceNonVirtualizedTable?: boolean;
}

export const TableWithHeader = ({
  tableHeader,
  columns,
  rows,
  onRowClick,
  dataTableType: dataTableType,
  section,
  forceNonVirtualizedTable,
}: TableWithHeaderProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const { searchTerm, hasTableResults } = useSearchContext();

  // Auto-expand/collapse table based on search results
  useEffect(() => {
    if (searchTerm && dataTableType && section) {
      const hasResults = hasTableResults(section, dataTableType);
      setIsExpanded(hasResults);
    }
  }, [searchTerm, dataTableType, section, hasTableResults]);

  // Reset expansion when search is cleared
  useEffect(() => {
    if (!searchTerm) {
      setIsExpanded(false);
    }
  }, [searchTerm]);

  return (
    <Stack
      sx={{ mr: '2px', height: '100%', backgroundColor: colors.grey[100], borderRadius: '8px' }}
    >
      <Stack
        sx={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 1,
          p: 1,
          cursor: 'pointer',
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <ExpandButtonSimple buttonSize="tiny" isExpanded={isExpanded} />
        <Typography variant="body-medium" color={colors.grey[900]}>
          {getNameWithPlural(tableHeader)}
        </Typography>
        <Stack alignItems="center" mt={'2px'}>
          <Typography variant="caption" color={colors.grey[500]}>
            {rows.length}
          </Typography>
        </Stack>
      </Stack>
      <Collapse in={isExpanded}>
        {rows.length < 10 || forceNonVirtualizedTable ? (
          <Stack
            p={1}
            overflow="hidden"
            data-table-type={dataTableType}
            sx={{ '.data-table-container': { overflowX: 'hidden' } }}
          >
            <DataTable
              tableId={'NOT_RELEVANT' as DataTableId}
              columns={columns}
              rows={rows}
              onRowClick={onRowClick}
              variant={DataTableVariant.medium}
            />
          </Stack>
        ) : (
          <Stack
            p={1}
            height="500px"
            overflow="hidden"
            data-table-type={dataTableType}
            sx={{ '& [data-virtuoso-scroller="true"]': { overflowX: 'hidden' } }}
          >
            <VirtualScrollDataTable
              tableId={'NOT_RELEVANT' as DataTableId}
              columns={columns}
              rows={rows}
              onRowClick={onRowClick}
              variant={DataTableVariant.medium}
              exposeVirtuosoRef={true}
            />
          </Stack>
        )}
      </Collapse>
    </Stack>
  );
};
